import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { formatDate, relativeTime } from "@/lib/format";
import { Panel } from "@/components/admin/Field";

const LABELS = {
  "content.save": "guardou o rascunho do site",
  "content.publish": "publicou o site",
  "content.discard": "descartou o rascunho",
  "category.create": "criou a categoria",
  "category.update": "editou a categoria",
  "category.delete": "eliminou a categoria",
  "category.reorder": "reordenou as categorias",
  "rule.create": "criou a regra",
  "rule.update": "editou a regra",
  "rule.delete": "eliminou a regra",
  "rule.reorder": "reordenou regras",
  "rules.intro": "editou a introdução das regras",
  "rules.sync": "importou as regras do Google Doc",
  "rules.autosync": "alterou a sincronização automática",
  "rules.autosync.run": "sincronização automática executada",
  "media.upload": "carregou o ficheiro",
  "media.delete": "removeu o ficheiro",
  "user.create": "criou a conta",
  "user.activate": "reativou a conta",
  "user.deactivate": "desativou a conta",
  "user.reset_password": "repôs a password de",
  "user.delete": "eliminou a conta",
  "user.change_password": "alterou a própria password",
  "store.sync": "atualizou os pacotes da loja Tebex",
  "auth.login": "iniciou sessão",
};

const COLORS = { content: "text-eden-orange", category: "text-eden-turquoise", rule: "text-eden-turquoise", rules: "text-eden-turquoise", media: "text-amber-300", user: "text-rose-300", auth: "text-eden-muted", store: "text-amber-300" };

function ActivityRow({ item }) {
  const kind = item.action.split(".")[0];
  const hideTarget = item.action === "auth.login" || item.action === "user.change_password";
  return (
    <li className="flex gap-4 px-5 py-3 text-sm" data-testid={`activity-${item.id}`}>
      <time dateTime={item.at} title={formatDate(item.at)} className="w-24 shrink-0 font-mono text-[11px] uppercase tracking-wider text-eden-muted">{relativeTime(item.at)}</time>
      <p className="min-w-0 flex-1 text-eden-secondary">
        <span className="font-medium text-eden-text">{item.actor}</span> <span className={COLORS[kind] || ""}>{LABELS[item.action] || item.action}</span>
        {!hideTarget && item.target && <> <span className="text-eden-text">“{item.target}”</span></>}
        {item.detail && <span className="text-eden-muted"> · {item.detail}</span>}
      </p>
    </li>
  );
}

export default function EditAtividade() {
  const [data, setData] = useState(null);
  const [actor, setActor] = useState("");
  const load = useCallback(() => api.get(`/admin/activity?limit=150${actor ? `&actor=${encodeURIComponent(actor)}` : ""}`).then(({ data: d }) => setData(d)).catch((e) => toast.error(errorMessage(e))), [actor]);
  useEffect(() => { load(); }, [load]);

  return (
    <Panel title="Registo de alterações" description="Quem alterou o quê e quando. Inclui edições de conteúdo, regras, ficheiros, equipa e inícios de sessão.">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-eden-secondary">
          Filtrar por pessoa
          <select value={actor} onChange={(e) => setActor(e.target.value)} className="input-eden w-auto" data-testid="activity-actor-filter">
            <option value="">Todas</option>
            {(data?.actors || []).map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <button type="button" onClick={load} className="btn-ghost h-9" data-testid="activity-refresh"><RefreshCw size={14} /> Atualizar</button>
      </div>
      {!data && <p className="text-sm text-eden-muted">A carregar…</p>}
      {data && data.items.length === 0 && <p className="text-sm text-eden-muted" data-testid="activity-empty">Ainda não há registos.</p>}
      {data && data.items.length > 0 && (
        <ul className="-mx-6 divide-y divide-eden-borderSubtle border-y border-eden-border sm:-mx-8" data-testid="activity-list">
          {data.items.map((it) => <ActivityRow key={it.id} item={it} />)}
        </ul>
      )}
    </Panel>
  );
}
